import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import achievementsData from '../../data/achievements.json'
import { fadeInUp, staggerContainer } from '../../utils/animations'
import { FaMedal, FaTrophy, FaCertificate, FaExternalLinkAlt, FaAward } from 'react-icons/fa'
import './achievement.css'

const iconMap = {
  certification: <FaCertificate />,
  course: <FaMedal />,
  competition: <FaTrophy />,
  award: <FaAward />,
  default: <FaMedal />
}

const getIcon = (type) => iconMap[type?.toLowerCase()] || iconMap.default

const Achievement = () => {
  const { certifications = [], achievements = [] } = achievementsData
  const [activeTab, setActiveTab] = useState('certifications')

  return (
    <motion.section
      id="achievement"
      className="achievement-section"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      {/* Header */}
      <motion.div
        className="achievement__hero"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <motion.span className="achievement__hero-tag" variants={fadeInUp}>
          <span className="achievement__hero-dot" /> Milestones
        </motion.span>
        <motion.h2 className="achievement__hero-title" variants={fadeInUp}>
          Achievements
        </motion.h2>
        <motion.p className="achievement__hero-sub" variants={fadeInUp}>
          Certifications, awards, and recognitions earned along the journey.
        </motion.p>

        
      </motion.div>

      {/* Tab Switcher */}
      <motion.div
        className="achievement__tabs"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <button
          className={`achievement__tab ${activeTab === 'certifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('certifications')}
        >
          <FaCertificate /> Certifications
        </button>
        <button
          className={`achievement__tab ${activeTab === 'achievements' ? 'active' : ''}`}
          onClick={() => setActiveTab('achievements')}
        >
          <FaTrophy /> Awards & Competitions
        </button>
      </motion.div>

      {/* Cards */}
      <div className="container achievement__container">
        <AnimatePresence mode="wait">

          {activeTab === 'certifications' && (
            <motion.div
              key="certifications"
              className="achievement__grid"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35 }}
            >
              {certifications.map((cert, index) => (
                <motion.div
                  key={cert.id}
                  className="achievement__card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="achievement__card-accent" />
                  <div className="achievement__card-top">
                    <span className="achievement__card-icon cert">
                      {getIcon(cert.type)}
                    </span>
                    <span className="achievement__card-type">{cert.type}</span>
                  </div>
                  <h3 className="achievement__card-title">{cert.name}</h3>
                  <p className="achievement__card-issuer">
                    <strong>Issuer:</strong> {cert.issuer}
                  </p>
                  {cert.date && (
                    <p className="achievement__card-date">{cert.date}</p>
                  )}
                  {cert.credential_url && cert.credential_url !== '' && (
                    <a
                      href={cert.credential_url}
                      target="_blank"
                      rel="noreferrer"
                      className="achievement__card-link"
                    >
                      <FaExternalLinkAlt /> View Credential
                    </a>
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeTab === 'achievements' && (
            <motion.div
              key="achievements"
              className="achievement__grid"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35 }}
            >
              {achievements.map((item, index) => (
                <motion.div
                  key={item.id}
                  className="achievement__card achievement__card--award"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="achievement__card-accent award" />
                  <div className="achievement__card-top">
                    <span className="achievement__card-icon award">
                      {getIcon(item.type)}
                    </span>
                    <span className="achievement__card-type award">{item.type}</span>
                  </div>
                  <h3 className="achievement__card-title">{item.title}</h3>
                  {item.organizer && (
                    <p className="achievement__card-issuer">
                      <strong>Organizer:</strong> {item.organizer}
                    </p>
                  )}
                  {item.date && (
                    <p className="achievement__card-date">{item.date}</p>
                  )}
                  {item.certificate && (
                    <span className="achievement__cert-badge">
                      <FaCertificate /> Certificate Available
                    </span>
                  )}
                  {item.credential_url && item.credential_url !== '' && (
                    <a
                      href={item.credential_url}
                      target="_blank"
                      rel="noreferrer"
                      className="achievement__card-link"
                    >
                      <FaExternalLinkAlt /> View Certificate
                    </a>
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </motion.section>
  )
}

export default Achievement
